import * as React from 'react';

import { IconCross, IconWarning, IconTick, IconInfo } from '@xing-com/icons';

import * as Styled from './status-banner.styles';
import type {
  StatusBannerProps,
  StatusBannerVariant,
} from './status-banner.types';
type StatusBannerPrimitiveProps = Omit<StatusBannerProps, 'onExited' | 'show'>;

const getLeadIcon = (
  variant: StatusBannerVariant
): ((props: React.SVGProps<SVGSVGElement>) => JSX.Element) => {
  switch (variant) {
    case 'success':
      return IconTick;
    case 'info':
      return IconInfo;
    default:
      return IconWarning;
  }
};

export const StatusBannerPrimitive = React.forwardRef<
  HTMLDivElement,
  StatusBannerPrimitiveProps
>(
  (
    {
      children,
      closeButtonProps,
      display = 'fixed',
      handleOnClose,
      showCloseButton = true,
      variant,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const LeadIcon = getLeadIcon(variant);

    return (
      <Styled.StatusBanner
        $display={display}
        $variant={variant}
        data-xds="StatusBanner"
        ref={forwardedRef}
        {...props}
      >
        <Styled.Content>
          <Styled.Icon $variant={variant}>
            <LeadIcon />
          </Styled.Icon>
          <Styled.Text $variant={variant} size="small">
            {children}
          </Styled.Text>
        </Styled.Content>

        {handleOnClose && showCloseButton ? (
          <Styled.Close
            $variant={variant}
            data-qa="close"
            data-testid="close"
            icon={IconCross}
            onClick={handleOnClose}
            size="medium"
            {...closeButtonProps}
          />
        ) : null}
      </Styled.StatusBanner>
    );
  }
);

StatusBannerPrimitive.displayName = 'StatusBanner (Primitive)';
